/**
 * ApprovalIntegrationPanel
 * 인사팀·재무 공용 결재 연동 탭 패널.
 * props:
 *   loadApprovals  : () => Promise<ApprovalResponse[]>
 *   infoBannerText : string
 */
import TablePaginator from "../common/TablePaginator";
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../util/api";
import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import AccessTimeRoundedIcon      from "@mui/icons-material/AccessTimeRounded";
import AssignmentRoundedIcon       from "@mui/icons-material/AssignmentRounded";
import BeachAccessRoundedIcon      from "@mui/icons-material/BeachAccessRounded";
import FlightRoundedIcon           from "@mui/icons-material/FlightRounded";
import PaymentsRoundedIcon         from "@mui/icons-material/PaymentsRounded";
import ShoppingCartRoundedIcon     from "@mui/icons-material/ShoppingCartRounded";

import dayjs           from "dayjs";
import "dayjs/locale/ko";
import { approvalApi } from "../../page/approval/api/approvalApi";
import { notify }      from "../../util/toast";
import { serverMsgOr } from "../../util/apiFeedback";
import { ds }          from "../../styles/designSystem";
import LoadingDots from "../common/LoadingDots";
import DetailModal from "../common/DetailModal";
// 결재 상세는 결재 시스템의 그 화면을 그대로 쓴다 — 여기 있던 복제본은 걷어냈다.
import ApprovalDetail from "../../page/approval/ApprovalDetail";

dayjs.locale("ko");

const BASE      = process.env.REACT_APP_API_REAL_URL;
const PAGE_SIZE = 5;

/* ── 공통 메타 ───────────────────────────────────── */
const STATUS_META = {
    PENDING:     { label: "대기",  color: "#94a3b8", bg: "#f1f5f9" },
    IN_PROGRESS: { label: "진행",  color: "#2563eb", bg: "#eff6ff" },
    APPROVED:    { label: "승인",  color: "#16a34a", bg: "#f0fdf4" },
    REJECTED:    { label: "반려",  color: "#dc2626", bg: "#fef2f2" },
    HELD:        { label: "보류",  color: "#d97706", bg: "#fffbeb" },
    // 회수가 빠져 있어 STATUS_META.PENDING 으로 떨어졌고, 회수된 문서가 «대기» 배지로 보였다.
    WITHDRAWN:   { label: "회수",  color: "#64748b", bg: "#f8fafc" },
};
const FORM_TYPES = [
    { type: "VACATION",      label: "휴가신청서",   icon: <BeachAccessRoundedIcon     sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "PAYMENT",       label: "대금지급요청", icon: <PaymentsRoundedIcon        sx={{ fontSize: 32 }} />, color: "#10b981" },
    { type: "PURCHASE",      label: "구매품의서",   icon: <ShoppingCartRoundedIcon     sx={{ fontSize: 32 }} />, color: "#f59e0b" },
    { type: "BUSINESS_TRIP", label: "출장신청서",   icon: <FlightRoundedIcon           sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "OVERTIME",      label: "초과근무신청", icon: <AccessTimeRoundedIcon       sx={{ fontSize: 32 }} />, color: "#ef4444" },
    { type: "HOLIDAY_WORK",  label: "휴일근무신청", icon: <AssignmentRoundedIcon       sx={{ fontSize: 32 }} />, color: "#9333ea" },
    { type: "OTHER",         label: "기타결재",     icon: <AssignmentRoundedIcon       sx={{ fontSize: 32 }} />, color: "#6b7280" },
];

/* ── 유틸 ─────────────────────────────────────────── */
/**
 * 더 이상 «내 차례» 가 아닌 상태 — 처리 대기 목록에서 빼고 결재 버튼을 잠근다.
 *
 * 회수(WITHDRAWN)가 빠져 있었다. 그래서 기안자가 회수한 문서가 «대기» 배지로 처리 대기 목록에
 * 그대로 뜨고 지출확정 버튼이 살아 있었다 — 사용자가 우회한 게 아니라 화면이 그렇게 안내했다.
 * 서버도 이제 회수 문서의 지출확정을 409 로 막지만(FinanceApprovalLinkService.loadForFinalAction),
 * 누를 수 있는 버튼을 보여주고 나서 거절하는 건 화면의 잘못이다.
 *
 * 종결 판정 기준은 서버 ApprovalStatus.isTerminal() + APPROVED·HELD. 여기 한 곳만 고치면 다음에
 * 또 다른 화면이 뚫리므로, 같은 뜻의 집합을 새로 만들지 말고 이 상수를 쓸 것.
 */
const COMPLETED = new Set(["APPROVED", "REJECTED", "HELD", "WITHDRAWN"]);
const isCompleted = (a) => COMPLETED.has(a.status);
const toComma     = (v) => { const n = String(v || "").replace(/,/g, "").replace(/[^\d]/g, ""); return n ? Number(n).toLocaleString("ko-KR") : ""; };
const fmtDate     = (v) => v ? dayjs(v).format("YYYY.MM.DD") : "—";
function getRowSummary(type, form) {
    const d = form || {};
    switch (type) {
        case "VACATION":
            if (d.vacationType === "반차") return `반차 · ${fmtDate(d.startDate)}`;
            if (d.startDate && d.endDate)  return `${fmtDate(d.startDate)} ~ ${fmtDate(d.endDate)}`;
            return d.vacationType || "";
        case "PAYMENT":
        case "PURCHASE": return d.amount ? `${toComma(d.amount)} 원` : "";
        case "BUSINESS_TRIP": return d.destination || "";
        case "OVERTIME": return d.date ? fmtDate(d.date) : "";
        case "HOLIDAY_WORK": return d.date ? `${fmtDate(d.date)}${d.hours ? ` · ${d.hours}시간` : ""}` : "";
        default: return "";
    }
}


/* ── ApprovalRow (한 줄) ─────────────────────────── */
const COL_BORDER = "1px solid rgba(0,0,0,0.06)";
const cell = (extra = {}) => ({
    px: 1.5, py: 0, display: "flex", alignItems: "center",
    borderRight: COL_BORDER, overflow: "hidden",
    ...extra,
});

function ApprovalRow({ approval, myId, selected, onClick }) {
    const m          = STATUS_META[approval.status] || STATUS_META.PENDING;
    const typeInfo   = FORM_TYPES.find(f => f.type === approval.type);
    const lineNames  = (approval.lines || []).map(l => l.approverName).join(" → ");
    const summary    = getRowSummary(approval.type, approval.form);
    const lastAction = [...(approval.lines || [])].filter(l => l.actionAt).pop();

    return (
        <Box onClick={onClick} sx={{
            display: "flex", alignItems: "stretch", cursor: "pointer", minHeight: 40,
            borderBottom: "1px solid #f1f5f9",
            borderLeft: selected ? "3px solid #2563eb" : "3px solid transparent",
            bgcolor: selected ? "#eff6ff" : "transparent",
            "&:hover": { bgcolor: selected ? "#eff6ff" : "#f8fafc" }, transition: "all 0.1s",
        }}>
            {/* 구분 */}
            <Box sx={{ ...cell(), flex: "0 0 110px" }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: m.color, mr: 0.75, flexShrink: 0 }} />
                {typeInfo && (
                    <Box sx={{ px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: 10, fontWeight: 700,
                        bgcolor: `${typeInfo.color}18`, color: typeInfo.color, whiteSpace: "nowrap" }}>
                        {typeInfo.label}
                    </Box>
                )}
            </Box>

            {/* 제목 · 신청자 */}
            <Box sx={{ ...cell({ flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }),
                flex: "0 0 240px", gap: 0.2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", width: "100%" }} noWrap>
                    {approval.title}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", width: "100%" }} noWrap>
                    {approval.submitterName}
                    {approval.submitterDept ? ` · ${approval.submitterDept}` : ""}
                </Typography>
            </Box>

            {/* 결재 라인 */}
            <Box sx={{ ...cell(), flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                    {lineNames ? `→ ${lineNames}` : "—"}
                </Typography>
            </Box>

            {/* 요약 */}
            <Box sx={{ ...cell(), flex: "0 0 110px" }}>
                <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 600 }} noWrap>
                    {summary || "—"}
                </Typography>
            </Box>

            {/* 상태 */}
            <Box sx={{ ...cell(), flex: "0 0 68px", justifyContent: "center" }}>
                <Box sx={{ px: 0.75, py: 0.15, borderRadius: 0.75, fontSize: 11, fontWeight: 700,
                    bgcolor: m.bg, color: m.color, whiteSpace: "nowrap" }}>
                    {m.label}
                </Box>
            </Box>

            {/* 기안일 */}
            <Box sx={{ ...cell(), flex: "0 0 110px", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                    {dayjs(approval.createdAt).format("YYYY-MM-DD")}
                    <br />
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{dayjs(approval.createdAt).format("HH:mm")}</span>
                </Typography>
            </Box>

            {/* 마지막 결재자 */}
            <Box sx={{ ...cell(), flex: "0 0 100px" }}>
                <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: lastAction ? 600 : 400 }} noWrap>
                    {lastAction ? lastAction.approverName : "—"}
                </Typography>
            </Box>

            {/* 결재일 */}
            <Box sx={{ ...cell({ borderRight: "none" }), flex: "0 0 110px", justifyContent: "center" }}>
                {lastAction ? (
                    <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                        {dayjs(lastAction.actionAt).format("YYYY-MM-DD")}
                        <br />
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>{dayjs(lastAction.actionAt).format("HH:mm")}</span>
                    </Typography>
                ) : (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>—</Typography>
                )}
            </Box>
        </Box>
    );
}

/* ── Section ─────────────────────────────────────── */
function Section({ title, allItems, myId, selected, onSelect }) {
    const [page, setPage] = useState(1);
    const pages = Math.ceil(allItems.length / PAGE_SIZE) || 1;
    const slice = allItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Paper sx={ds.card}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>{title}</Typography>
                <Chip label={`${allItems.length}건`} size="small"
                    sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700 }} />
            </Box>
            <Divider />
            {allItems.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center",
                    bgcolor: "#f8fafc", borderBottom: "1px solid #e8ecf0",
                    borderLeft: "3px solid transparent" }}>
                    {[
                        { label: "구분",         w: "0 0 110px", center: true },
                        { label: "제목 · 신청자", w: "0 0 240px", center: true },
                        { label: "결재 라인",    w: 1, center: true },
                        { label: "요약",       w: "0 0 110px", center: true },
                        { label: "상태",       w: "0 0 68px", center: true },
                        { label: "기안일",     w: "0 0 110px", center: true },
                        { label: "마지막 결재자", w: "0 0 100px", center: true },
                        { label: "결재일",     w: "0 0 110px", center: true, last: true },
                    ].map((col) => (
                        <Box key={col.label} sx={{
                            flex: col.w, px: 1.5, py: 0.75,
                            borderRight: col.last ? "none" : COL_BORDER,
                            display: "flex", justifyContent: col.center ? "center" : "flex-start",
                        }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>
                                {col.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            {slice.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography color="text.secondary" fontSize={13}>항목이 없습니다.</Typography>
                </Box>
            ) : (
                <>
                    {slice.map(a => (
                        <ApprovalRow key={a.id} approval={a} myId={myId}
                            selected={selected?.id === a.id} onClick={() => onSelect(a)} />
                    ))}
                    {allItems.length > PAGE_SIZE && (
                        <TablePaginator
                            totalPages={pages}
                            page={page}
                            onPageChange={setPage}
                        />
                    )}
                </>
            )}
        </Paper>
    );
}

/* ── 메인 패널 (exported) ────────────────────────── */
export default function ApprovalIntegrationPanel({ loadApprovals, infoBannerText, financeConfirm, loadAccounts }) {
    const [approvals, setApprovals] = useState([]);
    const [myId,      setMyId]      = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [selected,  setSelected]  = useState(null);   // 목록에서 고른 행 — 하이라이트용
    const [detail,    setDetail]    = useState(null);   // 그 문서의 상세
    const [accounts,  setAccounts]  = useState([]);   // 재무 지출확정 시 자금일보 반영 계좌

    const load = useCallback(() => {
        setLoading(true);
        loadApprovals()
            .then(data => setApprovals(Array.isArray(data) ? data : []))
            .catch(() => setApprovals([]))
            .finally(() => setLoading(false));
    }, [loadApprovals]);

    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (token) api.get(`${BASE}/api/employees/me`)
            .then(r => setMyId(r.id)).catch(() => {});
        load();
    }, [load]);

    useEffect(() => {
        if (typeof loadAccounts === "function") {
            loadAccounts().then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {});
        }
    }, [loadAccounts]);

    const pending  = approvals.filter(a => !isCompleted(a));
    const done     = approvals.filter(a => isCompleted(a));

    /**
     * 행을 열 때 <b>상세를 따로 받아 온다</b> — 목록 응답으로는 문서를 다 못 그린다.
     *
     * <p>목록 응답({@code ApprovalResponse.from})에는 그 문서에 박힌 양식 정의도, 열람자도 없다.
     * 그래서 목록 데이터를 그대로 상세에 넘기면 신규 양식으로 쓴 문서가 옛 렌더러로 떨어져
     * 반쯤 빈 문서로 보인다. 목록 데이터로 <b>먼저 열고</b> 상세가 도착하면 갈아끼운다 —
     * 기다리는 빈 모달을 만들지 않으려는 것이다.
     *
     * <p>다만 상세가 <b>끝내 안 오면 닫는다.</b> 예전엔 실패를 삼켜서({@code .catch(() => {})}) 목록 행이
     * 그대로 모달에 남았다. 그러면 위에 적은 «반쯤 빈 문서» 가 <b>영구히</b> 남는데, 상태·결재선·첨부는
     * 멀쩡히 보이므로 오류로 읽히지 않는다 — «데이터가 사라졌다» 는 신고로 돌아온다.
     * 실패는 실패로 보여야 한다.
     */
    const openDetail = (row) => {
        setSelected(row);
        setDetail(row);
        approvalApi.detail(row.id)
            .then(setDetail)
            .catch((e) => {
                setSelected(null);
                setDetail(null);
                notify(serverMsgOr("결재를 불러오지 못했습니다.")(e?.message), "error");
            });
    };
    const handleClose   = () => { setSelected(null); setDetail(null); };
    // 첨부·댓글은 모달을 연 채로 바뀐다 — 목록만 새로 받으면 열려 있는 문서가 옛 상태로 남는다.
    const handleRefresh = () => {
        load();
        if (selected) approvalApi.detail(selected.id).then(setDetail).catch(() => {});
    };

    if (loading) return <LoadingDots />;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={{ ...ds.cardPadded, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                <Typography sx={{ fontSize: 12, color: "#92400e" }}>{infoBannerText}</Typography>
            </Paper>

            <Section title="처리 대기 중" allItems={pending} myId={myId} selected={selected} onSelect={openDetail} />
            <Section title="처리 완료"    allItems={done}    myId={myId} selected={selected} onSelect={openDetail} />

            {/* 결재 상세는 결재 시스템과 <b>같은 화면</b>이다 — 복제본을 두면 한쪽만 고쳐진다. */}
            <DetailModal open={!!detail} onClose={handleClose} title="결재 상세">
                {detail && (
                    <ApprovalDetail key={detail.id} approval={detail} myId={myId}
                        onRefresh={handleRefresh} onClose={handleClose}
                        financeConfirm={financeConfirm} accounts={accounts} />
                )}
            </DetailModal>
        </Box>
    );
}
